import { ScrollView, YStack } from "tamagui";

import {
  Button,
  Footer,
  Heading,
  Image,
  Paragraph,
  Text,
  useImageAspectRatio,
} from "@/components";
import { BLUR_HASH } from "@/theme/constants";
import type { PrescriptionAnalysisResult } from "./usePrescriptionAnalysis";

interface PrescriptionResultProps {
  imageUri: string;
  result: Extract<PrescriptionAnalysisResult, { status: "success" }>;
  onContinue: () => void;
  isContinuePending?: boolean;
  onTryAgain: () => void;
}

export const PrescriptionResult = ({
  imageUri,
  result,
  onContinue,
  isContinuePending,
  onTryAgain,
}: PrescriptionResultProps) => {
  const { aspectRatio, onLoad } = useImageAspectRatio();

  return (
    <YStack flex={1}>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          padding: "$200",
          gap: "$200",
        }}
      >
        <Image
          source={{ uri: imageUri }}
          width="100%"
          maxWidth={400}
          aspectRatio={aspectRatio ?? 1.6}
          borderRadius="$150"
          backgroundColor="$neutral900"
          overflow="hidden"
          objectFit="cover"
          placeholder={{ blurhash: BLUR_HASH }}
          onLoad={onLoad}
        />

        <YStack gap="$100">
          <Heading size="$xl">Please confirm your medication</Heading>
          <Paragraph color="$textSecondary" size="$s">
            We read the following details from your prescription label. Make
            sure everything looks correct before continuing.
          </Paragraph>
          <YStack gap="$75" paddingTop="$100">
            <Text size="$s">
              Medication: {result.medication_name.toUpperCase()}
            </Text>
            <Text size="$s">Dosage: {result.dosage}</Text>
            <Text size="$s">Directions: {result.directions}</Text>
            {result.refills !== undefined && (
              <Text size="$s">Refills: {result.refills}</Text>
            )}
          </YStack>
        </YStack>
      </ScrollView>

      <Footer padding="$200" mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={onContinue}
          loading={isContinuePending}
          dataTrackingId="prescription-result-continue-button"
        >
          Continue
        </Button>
        <Button
          mode="tertiary"
          onPress={onTryAgain}
          dataTrackingId="prescription-result-try-again-button"
        >
          Try again
        </Button>
      </Footer>
    </YStack>
  );
};
